import React from 'react';
import { formatDateReadable } from '../utils/dateUtils';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotifications, Notification } from '../context/NotificationContext';
import { useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';

interface NotificationOverlayProps {
    isOpen: boolean;
    onClose: () => void;
    color?: string;
}

const NotificationOverlay: React.FC<NotificationOverlayProps> = ({ isOpen, onClose, color = 'bg-red-600' }) => {
    const { notifications, markAsRead, markAllAsRead, stopAlarm } = useNotifications();
    const { setView } = useContext(AppContext);
    const { currentUser } = useAuth();

    const handleNotificationClick = (notif: Notification) => {
        stopAlarm();
        markAsRead(notif.id);
        onClose();

        if (notif.type === 'order_update' || notif.title.toLowerCase().includes('commande')) {
            if (currentUser?.role === 'restaurateur' || currentUser?.role === 'super_admin') {
                // Déterminer l'onglet cible en fonction du message ou titre si possible, 
                // sinon par défaut sur 'pending' ou 'in_progress'
                let targetTab: 'pending' | 'in_progress' | 'sent' | 'completed' = 'pending';
                const msg = notif.message.toLowerCase();

                if (msg.includes('préparation') || msg.includes('acceptée')) targetTab = 'in_progress';
                else if (msg.includes('envoyée') || msg.includes('route')) targetTab = 'sent';
                else if (msg.includes('livrée') || msg.includes('terminée')) targetTab = 'completed';

                setView({ screen: 'adminOrders', params: { initialTab: targetTab } });
            } else if (currentUser?.role === 'livreur') {
                setView({ screen: 'livreurOrders' });
            } else {
                // Client
                setView({ screen: 'account' }); // Ou OrderScreen si on veut être précis
            }
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    className="fixed md:absolute inset-x-4 md:inset-x-auto md:right-0 top-20 md:top-full mt-2 md:w-80 bg-white rounded-2xl shadow-2xl overflow-hidden z-[100] origin-top md:origin-top-right border border-gray-100"
                >
                    <div className={`p-4 ${color} flex justify-between items-center`}>
                        <h3 className="font-bold text-white text-sm tracking-tight">Notifications</h3>
                        <button
                            onClick={(e) => { e.stopPropagation(); markAllAsRead(); }}
                            className="text-[10px] bg-white/20 text-white px-2 py-1 rounded-lg font-bold hover:bg-white/30 transition-colors uppercase tracking-wider"
                        >
                            Tout marquer lu
                        </button>
                    </div>
                    <div className="max-h-[70vh] overflow-y-auto no-scrollbar">
                        {notifications.length > 0 ? (
                            notifications.map((notif) => (
                                <div
                                    key={notif.id}
                                    onClick={() => handleNotificationClick(notif)}
                                    className={`p-4 border-b border-gray-50 cursor-pointer hover:bg-red-50/30 transition-all relative ${!notif.read ? 'bg-red-50/20' : ''}`}
                                >
                                    {!notif.read && (
                                        <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-red-600 rounded-full shadow-[0_0_8px_rgba(220,38,38,0.5)]"></div>
                                    )}
                                    <div className="flex justify-between items-start mb-1">
                                        <h4 className={`text-sm pr-2 ${!notif.read ? 'font-bold text-gray-900' : 'font-medium text-gray-600'}`}>
                                            {notif.title}
                                        </h4>
                                        <p className="text-[10px] font-bold opacity-60 mt-1 uppercase tracking-widest">
                                            {formatDateReadable(notif.timestamp)}
                                        </p>
                                    </div>
                                    <p className={`text-xs line-clamp-2 ${!notif.read ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                                        {notif.message}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <div className="p-12 text-center">
                                <span className="text-4xl mb-4 block grayscale opacity-30">🔔</span>
                                <p className="text-sm text-gray-400 font-medium italic">Aucune notification</p>
                            </div>
                        )}
                    </div>
                    {notifications.length > 0 && (
                        <div className="p-3 bg-gray-50 text-center border-t border-gray-100">
                            <button onClick={onClose} className="text-xs text-gray-500 font-bold hover:text-gray-700">Fermer</button>
                        </div>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default NotificationOverlay;
